import { forwardRef, RefObject } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Portal,
  Spacer,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { giftingConfirmFormSchema } from 'app/utils/schemas/gifting/giftingConfirmForm';
import { useTopUpWalletStore } from 'app/store';
import { useQueryClient } from '@tanstack/react-query';

import useRewardPoints from 'app/hooks/useRewardPoints';
import { useToast, CheckOutline, ArrowLeftOutline } from '@aslanpay/portal-common-lib';
import useAuthContext from 'app/hooks/useAuthContext';
import QUERY_KEYS from 'app/constants/queryKeys';

import { useParams } from 'react-router-dom';
import useFetchCompany from 'app/hooks/useFetchCompany';

import getPointsValue from 'app/utils/points';

import { formatCurrency } from 'app/utils/strings';

import formatNumber from 'app/utils/formatting';

import { ConfirmFields, TopUpWalletFields, GiftingConfirmTopUpFormProps } from '../types';

const GiftingConfirmTopUpWalletForm = forwardRef<HTMLElement, GiftingConfirmTopUpFormProps>(
  ({ id, onStepAction, onSuccess, previousStep, canPrevStep }, ref) => {
    const toast = useToast();
    const { companyId } = useParams();
    const { user } = useAuthContext();
    const { data: company } = useFetchCompany(companyId || user?.companyId);
    const topUpWalletData = useTopUpWalletStore((state) => state.topUpWalletData);
    const setTopUpWalletData = useTopUpWalletStore((state) => state.setTopUpWalletData);
    const resetTopUpWalletData = useTopUpWalletStore((state) => state.resetTopUpWalletData);

    const { pointsAmount } = topUpWalletData as TopUpWalletFields;

    const queryClient = useQueryClient();

    const {
      register,
      handleSubmit,
      getValues,
      formState: { errors, isValid },
    } = useForm<ConfirmFields>({
      mode: 'onChange',
      defaultValues: topUpWalletData as ConfirmFields,
      resolver: yupResolver(giftingConfirmFormSchema),
    });

    const note = `Company top up by ${user?.firstName} ${user?.lastName} (${user?.id})`;

    const { mutate: topUpCompanyWallet, isLoading: isToppingUpWallet } = useRewardPoints({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: `${pointsAmount} points successfully added to wallet`,
          description: `You can now gift these points to your employees.`,
        });

        // add invalidation to this section onSuccess
        queryClient.invalidateQueries({
          predicate: (query) =>
            [QUERY_KEYS.FETCH_POINTS_BAGS, QUERY_KEYS.FETCH_POINTS_TRANSACTIONS].includes(
              query.queryKey?.[0] as string
            ),
        });

        resetTopUpWalletData();

        onSuccess?.();

        onStepAction();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, the points could not be added, please try again.',
        });
      },
    });

    const handleTopUpWallet = () => {
      topUpCompanyWallet({
        individualIds: undefined,
        transactionReason: 'COMPANY_TOP_UP',
        pointsAmount: Number(pointsAmount),
        notes: note,
        companyId: company?.id || '',
        referralId: '',
      });
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setTopUpWalletData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleTopUpWallet)} noValidate>
        <Stack spacing="20px">
          <VStack align="start" spacing={2}>
            <Box
              backgroundColor="brand.lightGray2"
              borderWidth={1}
              borderStyle="solid"
              borderColor="brand.lightGray1"
              p="4"
              w="100%"
              borderRadius="8"
            >
              <Text textStyle="h5SemiBold" color="brand.black" mb="18px">
                Confirm your top up
              </Text>
              <HStack>
                <Text textStyle="smallMedium" color="brand.darkGray">
                  Points
                </Text>
                <Spacer />
                <Text textStyle="smallSemiBold" color="brand.mutedPurple" align="left">
                  {formatNumber(Number(pointsAmount))}
                </Text>
              </HStack>
              <Divider my={2} />
              <HStack>
                <Text textStyle="smallMedium" color="brand.darkGray" align="left">
                  Cost
                </Text>
                <Spacer />
                <Text textStyle="smallSemiBold" color="brand.mutedPurple" align="left">
                  {formatCurrency(getPointsValue(Number(pointsAmount)) ?? 0, 'GBP')}
                </Text>
              </HStack>
              <Divider my={2} />
              <HStack>
                <Text textStyle="smallMedium" color="brand.darkGray" align="left">
                  Rate
                </Text>
                <Spacer />
                <Text textStyle="smallSemiBold" color="brand.mutedPurple" align="left">
                  200 points = £1
                </Text>
              </HStack>
              <Divider my={2} />
              <HStack>
                <Text textStyle="smallMedium" color="brand.darkGray" align="left">
                  Payment method
                </Text>
                <Spacer />
                <Text textStyle="smallSemiBold" color="brand.mutedPurple" align="left">
                  Company account
                </Text>
              </HStack>
              <Divider my={2} />
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors?.confirmAuthorised?.message} isRequired w="100%">
            <HStack spacing="20px" alignItems="flex-start" w="100%">
              <Box
                bg="brand.lightGray2"
                borderWidth={1}
                borderStyle="solid"
                borderColor="brand.lightGray1"
                borderRadius="4"
                display="flex"
                alignItems="center"
                w="100%"
              >
                <Checkbox
                  {...register('confirmAuthorised')}
                  w="100%"
                  alignItems="center" // aligns checkbox icon with label text
                  lineHeight="1.2" // tightens text to reduce extra bottom space
                  pt="7px"
                  pl="8px" // nudges it down slightly to balance visually
                >
                  I am authorised to make this purchase.
                </Checkbox>
              </Box>
            </HStack>
            <FormErrorMessage>{errors?.confirmAuthorised?.message}</FormErrorMessage>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<CheckOutline boxSize="20px" />}
              isLoading={isToppingUpWallet}
              loadingText="Topping up wallet"
            >
              Confirm
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

GiftingConfirmTopUpWalletForm.displayName = 'GiftingConfirmTopUpWalletForm';

export default GiftingConfirmTopUpWalletForm;
